import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BuildingIcon, ChevronsUpDownIcon, ClipboardListIcon, KanbanSquareIcon, LayoutDashboardIcon, LayoutTemplateIcon, LogOutIcon, MenuIcon, PlugIcon, SettingsIcon, Trash2Icon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBrandTheme } from '../hooks/useBrandTheme';
import { Permission } from '../config/permissions';
import { ROLE_LABELS } from '../types';
import { BrandLogo } from '../components/shared/BrandLogo';
import { Avatar } from '../components/ui/Avatar';
import { Dropdown } from '../components/ui/Dropdown';
import { Drawer } from '../components/ui/Drawer';
interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  permission?: Permission;
}
const NAV: NavItem[] = [{
  to: '/app/dashboard',
  label: 'Dashboard',
  icon: LayoutDashboardIcon
}, {
  to: '/app/leads',
  label: 'Leads',
  icon: UsersIcon
}, {
  to: '/app/pipeline',
  label: 'Embudo',
  icon: KanbanSquareIcon
}, {
  to: '/app/formularios',
  label: 'Formularios',
  icon: ClipboardListIcon,
  permission: 'forms.manage'
}, {
  to: '/app/landings',
  label: 'Landings',
  icon: LayoutTemplateIcon,
  permission: 'landings.manage'
}, {
  to: '/app/papelera',
  label: 'Papelera',
  icon: Trash2Icon
}, {
  to: '/app/equipo',
  label: 'Equipo',
  icon: UsersRoundIcon,
  permission: 'team.manage'
}, {
  to: '/app/integraciones',
  label: 'Integraciones',
  icon: PlugIcon,
  permission: 'integrations.manage'
}, {
  to: '/app/configuracion',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings.manage'
}];
function navClass({
  isActive
}: {
  isActive: boolean;
}): string {
  return ['flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]', isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface hover:text-foreground'].join(' ');
}
export function AppLayout() {
  const {
    organization,
    user,
    role,
    hasPermission,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // La identidad visual de la empresa se aplica a toda la plataforma.
  useBrandTheme(organization);
  const items = NAV.filter(item => !item.permission || hasPermission(item.permission));
  async function handleLogout() {
    await logout();
    navigate('/login');
  }
  const nav = <nav className="flex flex-col gap-1" aria-label="Navegación principal" data-id="element-724" data-mp="35fnu64odri9b">
      {items.map(item => <NavLink key={item.to} to={item.to} className={navClass} onClick={() => setMenuOpen(false)} data-id="element-725" data-mp="30gb5rfhv234m">
          <item.icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-726" data-mp="2vij6u1istgqx" />
          {item.label}
        </NavLink>)}
    </nav>;
  const workspaceCard = <div className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5" data-id="element-727" data-mp="2qhvftfukkzwp">
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary/10" data-id="element-728" data-mp="2mhv0lsznckol">
        {organization?.brand.logoUrl ? <img src={organization.brand.logoUrl} alt="" className="h-6 w-6 object-contain" data-id="element-729" data-mp="160ci458yjr7k" /> : <BuildingIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-730" data-mp="162gy3lk4sgo7" />}
      </span>
      <span className="min-w-0 flex-1" data-id="element-731" data-mp="1axmey5yof55f">
        <span className="block truncate text-sm font-semibold text-foreground" data-id="element-732" data-mp="1fzbqrntq3muk">
          {organization?.name ?? 'Mi empresa'}
        </span>
        <span className="block truncate text-xs text-muted-foreground" data-id="element-733" data-mp="icuzp0bl25h9">
          {organization?.sector}
        </span>
      </span>
      <ChevronsUpDownIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-734" data-mp="s97szx37uhbh" />
    </div>;
  return <div className="flex min-h-screen w-full bg-surface" data-id="element-735" data-mp="x0g0uoowdumj">
      {/* Sidebar escritorio */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-none flex-col border-r border-border bg-background p-4 lg:flex" data-id="element-736" data-mp="3vq7rq7b89fmk">
        <div className="mb-6 px-1" data-id="element-737" data-mp="4ilj009nimyu">
          <BrandLogo src={organization?.brand.logoUrl ?? organization?.logoUrl} size={26} alt={organization?.name ?? 'Sistema AVA'} data-id="element-738" data-mp="4hk6drczlsf0" />
        </div>
        <div className="mb-4" data-id="element-739" data-mp="227qy71pbz5px">{workspaceCard}</div>
        {nav}
        <div className="mt-auto pt-4" data-id="element-740" data-mp="225eae7ww614y">
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface hover:text-foreground" data-id="element-741" data-mp="2fyvqfiqdcvq8">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-742" data-mp="2pvh22mww9531" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Menú móvil */}
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} side="left" title="Menú" data-id="element-743" data-mp="2yxzzgrhwuphw">
        <div className="space-y-4" data-id="element-744" data-mp="2tyn39g55g62n">
          {workspaceCard}
          {nav}
          <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-surface" data-id="element-745" data-mp="2twkfl2rgyp13">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-746" data-mp="zm6xshk6gvyu" />
            Cerrar sesión
          </button>
        </div>
      </Drawer>

      <div className="flex min-w-0 flex-1 flex-col" data-id="element-747" data-mp="3g6j0jzhcm4uj">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:px-6" data-id="element-748" data-mp="3g60wigog2mc2">
          <button type="button" onClick={() => setMenuOpen(true)} aria-label="Abrir menú" className="flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-surface lg:hidden" data-id="element-749" data-mp="36gr6d6d5vdql">
            <MenuIcon className="h-5 w-5" data-id="element-750" data-mp="2xguaajofzcli" />
          </button>
          <div className="min-w-0 flex-1" data-id="element-751" data-mp="2xe83wndaf34v">
            <p className="truncate text-sm font-semibold text-foreground" data-id="element-752" data-mp="2jhbe8p8wnrpk">
              {organization?.name}
            </p>
            <p className="truncate text-xs text-muted-foreground" data-id="element-753" data-mp="2niwpy79pdzy6">
              {role ? ROLE_LABELS[role] : ''}
            </p>
          </div>
          <Dropdown align="right" trigger={<span className="flex items-center gap-2 rounded-md px-1 py-1 hover:bg-surface" data-id="element-755" data-mp="lr1ritcgjrxw">
                <Avatar name={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`} size="sm" data-id="element-756" data-mp="1unyu89whphqf" />
                <span className="hidden text-sm font-medium text-foreground sm:inline" data-id="element-757" data-mp="1po40lylbye1o">
                  {user?.firstName}
                </span>
              </span>} items={[{
          label: 'Configuración de empresa',
          icon: <SettingsIcon className="h-4 w-4" data-id="element-758" data-mp="1gp8vyqxxpc0h" />,
          onSelect: () => navigate('/app/configuracion')
        }, {
          label: 'Cerrar sesión',
          icon: <LogOutIcon className="h-4 w-4" data-id="element-759" data-mp="29cv0q2iwemox" />,
          destructive: true,
          onSelect: handleLogout
        }]} data-id="element-754" data-mp="lqry0vs7f2ng" />
        </header>

        <main className="min-w-0 flex-1 px-4 py-8 sm:px-6 lg:px-10" data-id="element-760" data-mp="24kldiu9640qc">
          <Outlet data-id="element-761" data-mp="1znbfwrbmkirf" />
        </main>
      </div>
    </div>;
}